import { ChangeEvent, KeyboardEvent, useCallback, useEffect, useState } from 'react';

import { usePostHogContext } from '@/features/Tracking/context/PostHogContext';
import { hubspotInit } from '@/features/Tracking/utils/hubspotInit';
import { useAppDispatch, useAppSelector } from '@/hooks/redux-hooks';
import { useGxTokenFromStorage } from '@/services/storage';
import { loginGX, resetStatus } from '@/slices/authSlice';
import { emailValidator } from '@/utils/validators';

const useLoginComponent = () => {
  const dispatch = useAppDispatch();
  const [email, setEmail] = useState<string>('');
  const [error, setError] = useState<string>('');
  const [refreshReCaptcha, setRefreshReCaptcha] = useState(false);
  const [captchaToken, setCaptchaToken] = useState('');
  const { status } = useAppSelector(state => state.auth);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const gxToken = useGxTokenFromStorage();
  const onVerify = useCallback((token: string) => {
    setCaptchaToken(token);
  }, []);
  useEffect(() => {
    dispatch(resetStatus());
    return () => {
      dispatch(resetStatus());
    };
  }, [dispatch]);
  useEffect(() => {
    const intervalId = setInterval(() => {
      setRefreshReCaptcha(r => !r);
    }, 30000);
    return () => {
      clearInterval(intervalId);
    };
  }, []);
  useEffect(() => {
    const intervalId = setInterval(() => {
      if (gxToken) setIsLoggedIn(true);
    }, 4000);
    if (isLoggedIn) clearInterval(intervalId);
    return () => {
      clearInterval(intervalId);
    };
  }, [gxToken, isLoggedIn, status]);

  const identifyUserInHubSpot = (email: string) => {
    if (window._hsq) {
      hubspotInit(window.location.pathname, email);
    }
  };

  const validateEmail = () => {
    const errorMessage = emailValidator(email);
    if (errorMessage) setError(errorMessage);
    return !errorMessage;
  };

  const handleOnKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') handleLogin();
  };
  const { identifyUser } = usePostHogContext();

  const handleLogin = () => {
    if (validateEmail()) {
      identifyUserInHubSpot(email);
      identifyUser(email);
      dispatch(loginGX({ email, captchaToken }));
      setRefreshReCaptcha(r => !r);
    }
  };

  const handleChangeEmail = (e: ChangeEvent<HTMLInputElement>) => {
    if (error) {
      const errorMessage = emailValidator(email);
      if (errorMessage) setError(errorMessage);
      else {
        setError('');
      }
    }
    setEmail(e.target.value);
  };

  const resetPage = () => {
    setEmail('');
    setError('');
    setRefreshReCaptcha(r => !r);
    dispatch(resetStatus());
  };

  return {
    handleChangeEmail,
    handleOnKeyDown,
    status,
    error,
    handleLogin,
    email,
    resetPage,
    isLoggedIn,
    refreshReCaptcha,
    onVerify,
    captchaToken,
    setRefreshReCaptcha
  };
};

export default useLoginComponent;
